/**
 * ReprocessamentoPage (M10, RF-M10-004) — disparo e acompanhamento de
 * reprocessamento seletivo (admin). O formulário resolve o modo ("conferir de novo" ou
 * "ler de novo" com a versão — ADR-035) e o critério (filtro, lista ou todas); ao disparar,
 * guarda o reprocessamentoId e acompanha o status por polling (GET /reprocessamentos/{id}).
 * Feedback de 403/422 inline (Req 10 AC6).
 *
 * A guarda de perfil (só admin) vive na rota (ProtectedRoute) — RN-M10-001/PC-06.
 */

import { useState } from 'react';
import { ReprocessForm } from '../components/ReprocessForm';
import {
  useCriarReprocessamento,
  useStatusReprocessamento,
} from '../hooks/usePainel';
import { CriarReprocessamentoInput } from '../services/painel-types';
import { ApiError } from '../services/apiClient';

const ROTULO_STATUS: Record<string, string> = {
  enfileirado: 'Enfileirado',
  em_execucao: 'Em execução',
  concluido: 'Concluído',
  falha: 'Falha',
};

export function ReprocessamentoPage(): JSX.Element {
  const { criar, enviando } = useCriarReprocessamento();
  const [reprocessamentoId, setReprocessamentoId] = useState<string | null>(null);
  const [erroMsg, setErroMsg] = useState<string | null>(null);
  const { status } = useStatusReprocessamento(reprocessamentoId);

  async function disparar(input: CriarReprocessamentoInput): Promise<void> {
    setErroMsg(null);
    try {
      const resp = await criar(input);
      setReprocessamentoId(resp.reprocessamentoId);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 403) {
          setErroMsg('Seu perfil não tem permissão para disparar reprocessamento.');
        } else if (err.status === 422) {
          setErroMsg('Nenhuma visita corresponde ao filtro ou lista informada.');
        } else {
          setErroMsg('Não foi possível disparar o reprocessamento. Tente novamente.');
        }
      } else {
        setErroMsg('Não foi possível disparar o reprocessamento. Tente novamente.');
      }
    }
  }

  return (
    <div>
      <h1 className="page-title">Reprocessamento</h1>
      <p className="page-subtitle">
        Confere de novo (sem nova leitura, sem custo) ou lê de novo com uma versão de prompt as
        visitas de um filtro, de uma lista de IDs ou todas. As correções humanas nunca são
        sobrescritas.
      </p>

      <ReprocessForm enviando={enviando} onSubmit={(input) => void disparar(input)} />

      {erroMsg && <p className="notice">{erroMsg}</p>}

      {reprocessamentoId && (
        <section className="card">
          <h2>Acompanhamento</h2>
          {status ? (
            <table className="table">
              <tbody>
                <tr>
                  <th>Reprocessamento</th>
                  <td className="mono">{status.reprocessamentoId}</td>
                </tr>
                <tr>
                  <th>Status</th>
                  <td>
                    <span className="pill pill-muted">
                      {ROTULO_STATUS[status.status] ?? status.status}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th>Afetadas</th>
                  <td>{new Intl.NumberFormat('pt-BR').format(status.afetadas)}</td>
                </tr>
                <tr>
                  <th>Processadas</th>
                  <td>{new Intl.NumberFormat('pt-BR').format(status.processadas)}</td>
                </tr>
              </tbody>
            </table>
          ) : (
            <p className="muted">Carregando status…</p>
          )}
        </section>
      )}
    </div>
  );
}
